import { Box, Button, Stack, Typography } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { DEFAULT_AVAILABLE_POINTS, TICK, TICK_DURATION } from "../../../constants/constants";
import { getMaxAirQuality, getMaxCosts, getMaxGES, getMaxThermalComfort, getMaxVisualComfort, getMinAirQuality, getMinCosts, getMinGES, getMinThermalComfort, getMinVisualComfort } from "../../../constants/formulas";
import { computeScale } from "../../../constants/utils";
import { GameContext, IGameContext } from "../../../contexts/GameContext";
import { TaskStatus } from "../../../interface/Task";
import { Timestamp } from "../../../interface/Timestamp";
import AnimationPlayer from "./AnimationPlayer";
import ComfortBar from "./ComfortBar";
import QualityBar from "./QualityBar";
import RoundScore from "./RoundScore";
import { computeCumulAvg, computeValueAvg, computeValueTotal } from "../../../interface/Util";
import { AppContext, IAppContext } from "../../../contexts/AppContext";
import { addMinutes, displayTime } from "../../../interface/Time";
import { setStyle } from "../../../util";


interface Props {
    roundScores: Timestamp[],
    nextRoundFn: (newRoundScore: number) => void
}


const RoundResults = ({ roundScores, nextRoundFn }: Props) => {
    const { tasks, events, round, values, bounds, setValues, setTempIn, endTime, data,
        points, results, setResults, interfaceParams, maxPoints, scales, scores, setScores, stepDuration,
        disableAdvanceRound, setDisableAdvanceRound, setData } = useContext<IGameContext>(GameContext);

    const { socket } = useContext<IAppContext>(AppContext); 
    const { defaultValues } = useContext(AppContext);

    useEffect(() => {
        if (defaultValues) setStyle(defaultValues);
    })

    function onValuesReceived(val: any) {
        const SLEEP_TIME = endTime[round];
        const N_STEPS = val.length;

        for (let i = 0; i < N_STEPS; ++i) {
            // New scores
            const scoresClone = scores.slice();
            const timeStep = addMinutes(SLEEP_TIME, (i + 1) * stepDuration)
            if (timeStep.hours >= 24) { timeStep.hours -= 24; }
            const timestamp = displayTime(timeStep);
            const stepValues = val[i].values

            scoresClone[round].push({ time: timestamp, values: stepValues, score: val[i].score as number });
            setScores(scoresClone);

            // New results
            const resultsClone = results.slice();
            resultsClone[round]["timestamps"].push({
                timestamp: timestamp,
                data: {data, "electric_task": 0},
                values: {...stepValues, tempIn:val[i].tempIn},
                score: val[i].score as number
            });
            
            setResults(resultsClone);
        }

        // Reset cost and ges to 0 on interface.
        setValues(prev => ({...val[val.length - 1].values, ["cost"]: 0, ["ges"]: 0, }));
        setTempIn(val[val.length - 1].tempIn);

        setDisableAdvanceRound(false);

        socket?.reset_cost_ges()
    }

    useEffect(() => {
        socket?.bindFn((val: any) => { onValuesReceived(val) });
        // socket?.setDisconnectFn(onDisconnect);
    }, [])

    function computeAverage() {
        let sum = 0;

        roundScores.forEach((t: Timestamp) => {
            sum += t.score
        });

        return Math.round(sum / roundScores.length || 0);
    }

    function computeFinalScore() {
        let score = computeAverage();

        // tasks.forEach((t) => {
        //     if (t.status !== TaskStatus.DONE) { score -= t.penalty; }
        // });

        // events[round].forEach((e) => {
        //     e.tasks.forEach((t) => {
        //         if (t.status !== TaskStatus.DONE) { score -= t.penalty; }
        //     })
        // });

        return score;
    }

    /*
    function computeEnergyTotal() {
        const roundEnergy = computeValueTotal(results, "energy", round);
        const spentEnergy = (maxPoints[round] - points) * scales["energy"];
        const totalVal = roundEnergy + spentEnergy;
        return totalVal;
    }
    */

    return (
        <Container className="wrapper-resultat">
            <Box className="wrapper-top-panel">
                <Box className="container-top">
                    <Box className="container-top-left">
                        <Typography sx={{ m: 4 }} variant="h5">Résultats moyens pour la ronde {round + 1}</Typography>    
                    </Box> 
                    <Box className="container-top-right">
                        
                        <Box className="box-result"> 
                            <RoundScore roundScores={roundScores} events={events[round]} tasks={tasks}
                            average={computeAverage()} finalScore={computeFinalScore()} showScore={!disableAdvanceRound} />
                        </Box>                                               
                    </Box> 
                </Box>               
            </Box>
            <Body className="container-resultat">
                <MainWindow className="main-resultat">
                    <Row className="box-result"> 
                        <Title className="main-resultat-title">Confort</Title>
                        <Box className="box-result-stat">
                            <StatBox>
                                <AnimationPlayer asset={"air_quality"} caption={"Qualité de l'air"} isGameEnd={false}/>
                                {interfaceParams && results ? <QualityBar
                                    lowThreshold={interfaceParams["bars"]["air_quality"]["gradient"][1]["threshold"]}
                                    highThreshold={interfaceParams["bars"]["air_quality"]["gradient"][2]["threshold"]}
                                    currentScale={computeScale(computeValueAvg(results, "air_quality", round), bounds["air_quality"]["min"], bounds["air_quality"]["max"])}
                                    minLabel={interfaceParams["bars"]["air_quality"]["labels"][0]}
                                    maxLabel={interfaceParams["bars"]["air_quality"]["labels"][1]}
                                    asset={"air_quality"}
                                /> : null}
                            </StatBox>
                            <StatBox>
                                <AnimationPlayer asset={"visual_comfort"} caption={"Confort visuel"} isGameEnd={false}/>
                                {interfaceParams && results ? <ComfortBar
                                    lowerThreshold={interfaceParams["bars"]["visual_comfort"]["gradient"][1]["threshold"]}
                                    lowThreshold={interfaceParams["bars"]["visual_comfort"]["gradient"][2]["threshold"]}
                                    highThreshold={interfaceParams["bars"]["visual_comfort"]["gradient"][3]["threshold"]}
                                    higherThreshold={interfaceParams["bars"]["visual_comfort"]["gradient"][4]["threshold"]}
                                    currentScale={computeScale(computeValueAvg(results, "visual_comfort", round), bounds["visual_comfort"]["min"], bounds["visual_comfort"]["max"])}
                                    minLabel={interfaceParams["bars"]["visual_comfort"]["labels"][0]}
                                    maxLabel={interfaceParams["bars"]["visual_comfort"]["labels"][1]}
                                    asset={"visual_comfort"}
                                /> : null}
                            </StatBox>
                            <StatBox>
                                <AnimationPlayer asset={"thermal_comfort"} caption={"Confort thermique"} isGameEnd={false} />
                                {interfaceParams && results ? <ComfortBar
                                    lowerThreshold={interfaceParams["bars"]["thermal_comfort"]["gradient"][1]["threshold"]}
                                    lowThreshold={interfaceParams["bars"]["thermal_comfort"]["gradient"][2]["threshold"]}
                                    highThreshold={interfaceParams["bars"]["thermal_comfort"]["gradient"][3]["threshold"]}
                                    higherThreshold={interfaceParams["bars"]["thermal_comfort"]["gradient"][4]["threshold"]}
                                    currentScale={computeScale(computeValueAvg(results, "thermal_comfort", round), bounds["thermal_comfort"]["min"], bounds["thermal_comfort"]["max"])}
                                    minLabel={interfaceParams["bars"]["thermal_comfort"]["labels"][0]}
                                    maxLabel={interfaceParams["bars"]["thermal_comfort"]["labels"][1]}
                                    asset={"thermal_comfort"}
                                /> : null}
                            </StatBox>
                        </Box>
                    </Row>
                    <Row className="box-result">
                        <Title className="main-resultat-title">Performance énergétique</Title>
                        <Box className="box-result-stat">
                            <StatBox>
                                <AnimationPlayer asset={"energy"} caption={"Consommation d'énergie"} isGameEnd={false} />
                                {interfaceParams && results ? <QualityBar
                                    lowThreshold={interfaceParams["bars"]["energy"]["gradient"][1]["threshold"]}
                                    highThreshold={interfaceParams["bars"]["energy"]["gradient"][2]["threshold"]}
                                    currentScale={computeScale(computeValueAvg(results, "energy", round), bounds["energy"]["min"], bounds["energy"]["max"])}
                                    minLabel={interfaceParams["bars"]["energy"]["labels"][0]}
                                    maxLabel={interfaceParams["bars"]["energy"]["labels"][1]}
                                    asset="energy"
                                /> : null}
                            </StatBox>
                            <StatBox>
                                <AnimationPlayer asset={"cost"} caption={"Coûts"} isGameEnd={false} />
                                {interfaceParams && results ? <QualityBar
                                    lowThreshold={interfaceParams["bars"]["cost"]["gradient"][1]["threshold"]}
                                    highThreshold={interfaceParams["bars"]["cost"]["gradient"][2]["threshold"]}
                                    currentScale={computeScale(computeCumulAvg(results, "cost", round), bounds["cost"]["min"], bounds["cost"]["max"])}
                                    minLabel={interfaceParams["bars"]["cost"]["labels"][0]}
                                    maxLabel={interfaceParams["bars"]["cost"]["labels"][1]}
                                    asset="cost"
                                /> : null}
                            </StatBox>
                        </Box>
                    </Row>
                </MainWindow>

                <Stack className="container-btn">
                    <Button variant="contained" size="large" style={{ "fontSize": ".9rem" }} disabled={disableAdvanceRound} onClick={() => { nextRoundFn(computeFinalScore()) }}>{round!=2?"Ronde suivante":"Voir les résultats finaux"}</Button>
                </Stack>
            </Body>


        </Container>
    );
};


const Container = styled.div`
`;

const Body = styled.div`
    display: flex;
`

const MainWindow = styled.div`
`;

const Title = styled.div`
    display: flex;
    padding: .5em;
`;

const Row = styled.div`
    width: 100%;
    display: flex;
`;

const StatBox = styled.div`
    flex-grow: 1;
`


export default RoundResults;